// SPDX-License-Identifier: AGPL-3.0-or-later

export const UPDATE_PROMPT_SNOOZE_MS = 4 * 60 * 60 * 1000;

export type UpdatePromptKind = 'web' | 'native' | 'module';

export interface UpdatePromptTarget {
	readonly kind: UpdatePromptKind;
	readonly versionKey: string;
}

export interface UpdatePromptRecord {
	readonly versionKey: string;
	readonly dismissed: boolean;
	readonly snoozedUntil: number | null;
	readonly snoozeCount: number;
}

export interface UpdatePromptSignals {
	readonly nativeReady: boolean;
	readonly nativeInstalling: boolean;
	readonly nativeVersion: string | null;
	readonly moduleUpdate: {readonly modules: ReadonlyArray<string>} | null;
	// Module updates carry no version, so their key is scoped to the running app session.
	readonly session: string;
	readonly webAvailable: boolean;
	readonly webVersion: string | null;
}

export function resolveUpdatePromptTarget(signals: UpdatePromptSignals): UpdatePromptTarget | null {
	if (signals.nativeInstalling) return null;
	if (signals.moduleUpdate) {
		const modules = [...signals.moduleUpdate.modules].sort().join(',');
		return {kind: 'module', versionKey: `module:${signals.session}:${modules}`};
	}
	if (signals.nativeReady) return {kind: 'native', versionKey: `native:${signals.nativeVersion ?? 'unknown'}`};
	if (signals.webAvailable) return {kind: 'web', versionKey: `web:${signals.webVersion ?? 'unknown'}`};
	return null;
}

export function isUpdatePromptVisible(
	target: UpdatePromptTarget,
	record: UpdatePromptRecord | null,
	now: number,
): boolean {
	if (!record || record.versionKey !== target.versionKey) return true;
	if (record.dismissed) return false;
	return record.snoozedUntil == null || now >= record.snoozedUntil;
}

// "Later" hides the prompt for the snooze window and reminds once more; a second "Later" retires it for that version.
export function snoozeUpdatePrompt(
	target: UpdatePromptTarget,
	record: UpdatePromptRecord | null,
	now: number,
): UpdatePromptRecord {
	const snoozeCount = record?.versionKey === target.versionKey ? record.snoozeCount + 1 : 1;
	if (snoozeCount > 1) return {versionKey: target.versionKey, dismissed: true, snoozedUntil: null, snoozeCount};
	return {versionKey: target.versionKey, dismissed: false, snoozedUntil: now + UPDATE_PROMPT_SNOOZE_MS, snoozeCount};
}

export function dismissUpdatePrompt(target: UpdatePromptTarget): UpdatePromptRecord {
	return {versionKey: target.versionKey, dismissed: true, snoozedUntil: null, snoozeCount: 0};
}
